import { Table } from "antd";
import styled from "styled-components";
import { StyledFormTableInput } from "../../../../components/atoms/Input/Input.styles";
import { addItemsTableStripeRowClassName } from "../../../shared/orderLineForm/components/addItemsPickerStyles";

/**
 * Lines a Fulfil step can act on, with row tick boxes and (optionally) a
 * quantity — the same striped table as the purchase order Add items drawer.
 *
 * @param {{
 *   rows: !Array<!Object>,
 *   idKey: string,
 *   columns: !Array<{key: string, label: string, render: function(!Object, !Object): *, numeric?: boolean, width?: number}>,
 *   selection: !Object,
 *   qtyLabel?: string,
 *   disabled?: boolean,
 *   ariaLabel: string,
 * }} props `selection` comes from `useStepSelection`.
 */
export const FulfilLineGrid = ({
  rows,
  idKey,
  columns,
  selection,
  qtyLabel,
  disabled = false,
  ariaLabel,
}) => {
  const { checked, quantities, setSelected, setQty, errorFor } = selection;

  const tableColumns = columns.map(column => ({
    title: column.label,
    key: column.key,
    width: column.width,
    align: column.numeric ? "right" : "left",
    render: (_, row) => column.render(row, { isChecked: checked.has(row[idKey]) }),
  }));

  if (qtyLabel) {
    tableColumns.push({
      title: qtyLabel,
      key: "qty",
      width: 140,
      render: (_, row) => {
        const id = row[idKey];
        const error = errorFor(row);
        return (
          <>
            <QtyInput
              type="text"
              inputMode="decimal"
              value={quantities[id] ?? ""}
              disabled={disabled || !checked.has(id)}
              $invalid={Boolean(error)}
              aria-invalid={Boolean(error)}
              aria-label={`${qtyLabel} for ${row.product?.number || id}`}
              onChange={event => setQty(id, event.target.value)}
            />
            {error ? <QtyError>{error}</QtyError> : null}
          </>
        );
      },
    });
  }

  return (
    <Table
      size="small"
      aria-label={ariaLabel}
      rowKey={idKey}
      dataSource={rows}
      columns={tableColumns}
      pagination={false}
      scroll={{ y: "calc(100vh - 420px)", x: 640 }}
      rowClassName={(_, index) => addItemsTableStripeRowClassName(index)}
      rowSelection={{
        selectedRowKeys: [...checked],
        onChange: keys => setSelected(keys),
        getCheckboxProps: row => ({
          disabled,
          "aria-label": `Include ${row.product?.number || row.name || row[idKey]}`,
        }),
      }}
    />
  );
};

/**
 * "SKU-123 · Mug" cell.
 *
 * @param {?{number?: string, name?: string}} product
 * @return {string}
 */
export const productLabel = product =>
  [product?.number, product?.name].filter(Boolean).join(" · ") || "—";

const QtyInput = styled(StyledFormTableInput)`
  border-color: ${({ $invalid }) => ($invalid ? "#cf324a" : undefined)};
  height: 32px;
  padding: 4px 8px;
  margin-bottom: 0;
`;

const QtyError = styled.span`
  display: block;
  margin-top: 2px;
  color: #cf324a;
  font-size: 11px;
`;
